import { access } from 'node:fs/promises'
import { join } from 'node:path'
import { invariantResponse } from '@epic-web/invariant'
import { formatDistanceToNow } from 'date-fns'
import { getMDXComponent } from 'mdx-bundler/client/index.js'
import { type LoaderFunctionArgs, data, useLoaderData } from 'react-router'
import { prisma } from '#app/utils/db.server.ts'
import { reader } from '#app/utils/keystatic-reader.server'
import { parseMdx } from '#app/utils/mdx-bundler.server'

async function findPublicPdf(type: string, date: string, executive = false) {
	const directory = executive
		? join('public', type, 'executive')
		: join('public', type)
	const candidate = join(directory, `${date}.pdf`)
	try {
		await access(candidate)
		return `/${type}${executive ? '/executive' : ''}/${date}.pdf`
	} catch {
		return null
	}
}

export async function loader({ params }: LoaderFunctionArgs) {
	const type = params.type
	const date = params.date

	invariantResponse(date, 'Date param required', { status: 400 })
	invariantResponse(
		type === 'agenda' || type === 'minutes' || type === 'financials',
		`Meeting type must be agenda, minutes, or financials`,
		{ status: 400 },
	)

	// Financials are PDF-only
	if (type === 'financials') {
		const pdfUrl = await findPublicPdf('financials', date, false)
		return data({
			date,
			type,
			document: null,
			mdx: null,
			timeAgo: null,
			pdfUrl,
		})
	}

	const meeting = await prisma.meeting.findFirst({
		where: { date },
		select: { executive: true },
	})
	const isExecutive = !!meeting?.executive

	const collection = type === 'minutes' ? 'minutes' : 'agenda'
	const document = await reader.collections[collection].read(date, {
		resolveLinkedFiles: true,
	})
	const pdfUrl = !document ? await findPublicPdf(type, date, isExecutive) : null

	if (!document && !pdfUrl) {
		return data({
			date,
			type,
			document: null,
			mdx: null,
			timeAgo: null,
			pdfUrl: null,
		})
	}

	if (document) {
		const publishedOn = new Date(document.publishedOn)
		const timeAgo = formatDistanceToNow(publishedOn)

		const { code: mdx } = await parseMdx(document.content)
		invariantResponse(mdx, `Error parsing MDX file.`, { status: 404 })

		return data({ date, type, document, mdx, timeAgo, pdfUrl: null })
	}

	return data({ date, type, document: null, mdx: null, timeAgo: null, pdfUrl })
}

export default function MeetingDocumentRoute() {
	const data = useLoaderData<typeof loader>()
	const { type, date, document, mdx, timeAgo, pdfUrl } = data

	if (pdfUrl) {
		return (
			<div className="h-full min-h-[240px] p-2">
				<embed
					src={pdfUrl}
					type="application/pdf"
					className="h-[calc(100vh-22rem)] w-full rounded-md border"
				/>
			</div>
		)
	}

	if (!document || !mdx || !timeAgo) {
		return (
			<div className="flex min-h-[240px] items-center justify-center p-8 text-center">
				<p className="text-muted-foreground text-lg font-medium">
					No {type} document for {date} yet.
				</p>
			</div>
		)
	}

	const Component = getMDXComponent(mdx)

	return (
		<div className="prose prose-zinc dark:prose-invert lg:prose-lg max-w-none p-6">
			<div className="mb-4 flex items-center gap-2 text-sm text-slate-500">
				<span>Last Modified {timeAgo} ago</span>
			</div>
			<Component />
		</div>
	)
}
